import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { API_URL } from '../config/api';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const verifyCalledRef = useRef(false);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link. No token provided.');
      return;
    }

    // Guard against React Strict Mode double-mount
    if (verifyCalledRef.current) return;
    verifyCalledRef.current = true;

    const verify = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/auth/verify-email?token=${token}`);
        setStatus('success');
        setMessage(res.data.message || 'Email verified! Your account is now active.');
      } catch (err) {
        setStatus('error');
        setMessage(err.response?.data?.detail || 'Verification failed. The link may be invalid or expired.');
      }
    };

    verify();
  }, [token]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-900 via-black to-zinc-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md text-center">
        <h1 className="text-3xl sm:text-4xl font-bold text-red-600 mb-8">CASITAPLEX</h1>

        <div className="bg-zinc-800 rounded-xl shadow-2xl p-6 sm:p-8">
          {status === 'loading' && (
            <div className="space-y-4" data-testid="verify-loading">
              <Loader2 className="h-12 w-12 text-red-500 mx-auto animate-spin" />
              <h2 className="text-xl font-bold text-white">Verifying your email...</h2>
              <p className="text-gray-400 text-sm">Please wait while we activate your account.</p>
            </div>
          )}

          {status === 'success' && (
            <div className="space-y-4" data-testid="verify-success">
              <div className="w-16 h-16 mx-auto rounded-full bg-green-600/20 flex items-center justify-center">
                <CheckCircle2 className="h-10 w-10 text-green-400" />
              </div>
              <h2 className="text-xl font-bold text-white">Email Verified!</h2>
              <p className="text-gray-400 text-sm">{message}</p>
              <Button
                onClick={() => navigate('/login')}
                className="bg-red-600 hover:bg-red-700 text-white font-bold px-8 h-11 mt-2"
                data-testid="verify-login-btn"
              >
                Log In Now
              </Button>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-4" data-testid="verify-error">
              <div className="w-16 h-16 mx-auto rounded-full bg-red-600/20 flex items-center justify-center">
                <XCircle className="h-10 w-10 text-red-400" />
              </div>
              <h2 className="text-xl font-bold text-white">Verification Failed</h2>
              <p className="text-gray-400 text-sm">{message}</p>
              <Button
                onClick={() => navigate('/login')}
                className="bg-zinc-700 hover:bg-zinc-600 text-white px-8 h-11 mt-2"
                data-testid="verify-back-btn"
              >
                Back to Login
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
